import { ClassicPreset, NodeEditor } from 'rete';
import { AreaExtensions, AreaPlugin } from 'rete-area-plugin';
import {
  ConnectionPlugin,
  Presets as ConnectionPresets,
} from 'rete-connection-plugin';
import { Presets, VuePlugin } from 'rete-vue-plugin';
import FlowNode from './FlowNode.vue';

const SOCKET_NAME = 'flow';
const DEFAULT_NODE_SIZE = {
  width: 392,
  height: 236,
};

const MAX_INTERACTIVE_BUTTONS = 3;
const MAX_INTERACTIVE_SECTIONS = 10;
const MAX_INTERACTIVE_ROWS = 10;

const TOOL_NODE_LABELS = {
  text: 'Text',
  image: 'Image',
  video: 'Video',
  audio: 'Audio',
  file: 'File',
  location: 'Location',
  condition: 'Condition',
  template: 'Template Message',
  button: 'Button',
  list_message: 'Quick Reply',
  section: 'Section',
  row: 'Row',
  commerce: 'E-commerce',
};

const HORIZONTAL_GAP = 460;
const VERTICAL_GAP = 300;
const SPAWN_VERTICAL_GAP = 260;

const withNodeDefaults = (node, type, data = {}) => {
  node.width = DEFAULT_NODE_SIZE.width;
  node.height = DEFAULT_NODE_SIZE.height;
  node.selected = false;
  node.data = { type, ...data };
  return node;
};

const replaceOutputs = (node, outputs, socket) => {
  Object.keys(node.outputs || {}).forEach(key => {
    delete node.outputs[key];
  });

  outputs.forEach(({ key, label }) => {
    node.addOutput(key, new ClassicPreset.Output(socket, label));
  });
};

const normalizeInteractiveButtons = buttons =>
  Array.isArray(buttons) ? buttons.slice(0, MAX_INTERACTIVE_BUTTONS) : [];

const normalizeInteractiveSections = sections => {
  const normalizedSections = Array.isArray(sections)
    ? sections.slice(0, MAX_INTERACTIVE_SECTIONS)
    : [];

  const result = [];
  let totalRows = 0;

  normalizedSections.forEach((section, sectionIndex) => {
    if (totalRows >= MAX_INTERACTIVE_ROWS) return;

    const rawRows = Array.isArray(section?.rows) ? section.rows : [];
    const rows = rawRows
      .slice(0, MAX_INTERACTIVE_ROWS - totalRows)
      .map((row, rowIndex) => ({
        id: row?.id || `row_${sectionIndex + 1}_${rowIndex + 1}`,
        title: row?.title || `Row ${totalRows + rowIndex + 1}`,
        description: row?.description || '',
      }));

    totalRows += rows.length;

    result.push({
      id: section?.id || `section_${sectionIndex + 1}`,
      title: section?.title || '',
      rows,
    });
  });

  return result;
};

const syncInteractivePayload = node => {
  const subtype = node.data?.subtype === 'list' ? 'list' : 'buttons';

  node.data = {
    ...node.data,
    subtype,
    buttons: normalizeInteractiveButtons(node.data?.buttons),
    listButtonLabel: node.data?.listButtonLabel || '',
    sections: normalizeInteractiveSections(node.data?.sections),
  };
};

const syncInteractiveOutputs = (node, socket) => {
  syncInteractivePayload(node);
  const outputs = [
    { key: 'next', label: 'Next' },
    { key: 'buttons', label: 'Buttons' },
    { key: 'list', label: 'List Messages' },
    { key: 'commerce', label: 'E-commerce' },
  ];

  replaceOutputs(node, outputs, socket);
};

const createButtonNode = socket => {
  const node = withNodeDefaults(new ClassicPreset.Node('Button'), 'button', {
    title: 'Button',
    buttonGenerationMethod: 'static',
    buttonTitle: '',
    buttonId: '',
    actionType: 'start_flow',
    targetFlowId: '',
    labels: [],
    assignTeamId: '',
    assignUserId: '',
    webhookUrl: '',
    body: '',
  });

  node.addInput('reply', new ClassicPreset.Input(socket, 'Reply'));
  node.addOutput('next', new ClassicPreset.Output(socket, 'Next'));

  return node;
};

const createListMessageNode = socket => {
  const node = withNodeDefaults(
    new ClassicPreset.Node('Quick Reply'),
    'list_message',
    {
      title: 'Quick Reply',
      buttonLabel: '',
    }
  );

  node.addInput('message', new ClassicPreset.Input(socket, 'Message'));
  node.addOutput('section', new ClassicPreset.Output(socket, 'Section'));

  return node;
};

const createSectionNode = socket => {
  const node = withNodeDefaults(new ClassicPreset.Node('Section'), 'section', {
    title: 'Section',
    sectionTitle: '',
    body: '',
  });

  node.addInput('reply', new ClassicPreset.Input(socket, 'Reply'));
  node.addOutput('rows', new ClassicPreset.Output(socket, 'Rows'));

  return node;
};

const createRowNode = socket => {
  const node = withNodeDefaults(new ClassicPreset.Node('Row'), 'row', {
    title: 'Row',
    rowGenerationMethod: 'static',
    rowTitle: '',
    rowId: '',
    description: '',
    addLabels: [],
    removeLabels: [],
    assignTeamId: '',
    assignUserId: '',
    webhookUrl: '',
  });

  node.addInput('reply', new ClassicPreset.Input(socket, 'Reply'));
  node.addOutput('next', new ClassicPreset.Output(socket, 'Next'));

  return node;
};

const createCommerceNode = socket => {
  const node = withNodeDefaults(
    new ClassicPreset.Node('E-commerce'),
    'commerce',
    {
      title: 'E-commerce',
      body: '',
    }
  );

  node.addInput('reply', new ClassicPreset.Input(socket, 'Reply'));
  node.addOutput('next', new ClassicPreset.Output(socket, 'Next'));

  return node;
};

const createStartNode = socket => {
  const node = withNodeDefaults(
    new ClassicPreset.Node('Start Bot Flow'),
    'start',
    {
      triggerMode: 'no_match',
      matchType: 'exact',
      keyword: '',
      labels: [],
      assignTeamId: '',
      assignUserId: '',
      webhookUrl: '',
    }
  );

  node.addOutput(
    'compose',
    new ClassicPreset.Output(socket, 'Compose Next Message')
  );

  return node;
};

const createInteractiveNode = socket => {
  const node = withNodeDefaults(
    new ClassicPreset.Node('Interactive'),
    'interactive',
    {
      title: 'Interactive',
      headerType: 'none',
      headerText: '',
      body: '',
      footer: '',
    }
  );

  node.addInput('reply', new ClassicPreset.Input(socket, 'Reply'));
  syncInteractiveOutputs(node, socket);

  return node;
};

const createMessageNode = (socket, type) => {
  const isTemplateNode = type === 'template';
  const node = withNodeDefaults(
    new ClassicPreset.Node(TOOL_NODE_LABELS[type] || 'Message'),
    type,
    isTemplateNode
      ? {
          title: 'Template Message',
          body: '',
          templateId: '',
          templateName: '',
          templateLanguage: '',
          templateCategory: '',
          templateData: null,
          templateParams: {},
          renderedTemplate: '',
        }
      : {
          title: TOOL_NODE_LABELS[type] || 'Message',
          body: '',
          ...(type === 'text'
            ? {
                typingOnDisplay: false,
                delaySeconds: 0,
                delayMinutes: 0,
                delayHours: 0,
              }
            : {}),
        }
  );

  node.addInput('reply', new ClassicPreset.Input(socket, 'Reply'));
  node.addOutput('next', new ClassicPreset.Output(socket, 'Next'));

  return node;
};

const createNodeByType = (socket, type) => {
  if (type === 'start') return createStartNode(socket);
  if (type === 'interactive') return createInteractiveNode(socket);
  if (type === 'button') return createButtonNode(socket);
  if (type === 'list_message') return createListMessageNode(socket);
  if (type === 'section') return createSectionNode(socket);
  if (type === 'row') return createRowNode(socket);
  if (type === 'commerce') return createCommerceNode(socket);
  return createMessageNode(socket, type);
};

const serializeNode = (area, node) => {
  const view = area.nodeViews.get(node.id);

  return {
    id: node.id,
    type: node.data?.type || node.label,
    label: node.label,
    position: view ? { ...view.position } : { x: 0, y: 0 },
    data: {
      ...node.data,
    },
  };
};

const getNodeById = (editor, nodeId) =>
  editor.getNodes().find(node => node.id === nodeId);

const getFirstInputKey = node => Object.keys(node.inputs || {})[0] || 'reply';
const getStartNodes = editor =>
  editor.getNodes().filter(node => node.data?.type === 'start');

const canConnectNodes = ({
  sourceNode,
  sourceOutput,
  targetNode,
  targetInput,
}) => {
  if (!sourceNode || !targetNode) return false;
  if (sourceNode.id === targetNode.id) return false;
  if (targetNode.data?.type === 'start') return false;
  if (!sourceNode.outputs?.[sourceOutput]) return false;
  if (!targetNode.inputs?.[targetInput]) return false;

  if (sourceNode.data?.type === 'start') {
    return sourceOutput === 'compose';
  }

  if (sourceNode.data?.type === 'interactive') {
    if (sourceOutput === 'buttons') return targetNode.data?.type === 'button';
    if (sourceOutput === 'list') {
      return targetNode.data?.type === 'list_message';
    }
    if (sourceOutput === 'commerce') {
      return targetNode.data?.type === 'commerce';
    }
    return sourceOutput === 'next';
  }

  if (sourceNode.data?.type === 'list_message') {
    return sourceOutput === 'section' && targetNode.data?.type === 'section';
  }

  if (sourceNode.data?.type === 'section') {
    return sourceOutput === 'rows' && targetNode.data?.type === 'row';
  }

  return sourceOutput === 'next';
};

export const createReteFlowBuilder = async (
  container,
  { onContextMenu = () => {}, onSelectionChange = () => {} } = {}
) => {
  const editor = new NodeEditor();
  const area = new AreaPlugin(container);
  const connection = new ConnectionPlugin();
  const render = new VuePlugin();
  const socket = new ClassicPreset.Socket(SOCKET_NAME);

  render.addPreset(
    Presets.classic.setup({
      customize: {
        node() {
          return FlowNode;
        },
      },
    })
  );
  connection.addPreset(ConnectionPresets.classic.setup());

  editor.use(area);
  area.use(connection);
  area.use(render);
  AreaExtensions.simpleNodesOrder(area);

  let selectedNodeId = null;

  const notifySelection = () => {
    const node = selectedNodeId ? getNodeById(editor, selectedNodeId) : null;

    onSelectionChange(node ? serializeNode(area, node) : null);
  };

  const setSelectedNode = async nodeId => {
    editor.getNodes().forEach(node => {
      node.selected = node.id === nodeId;
    });

    selectedNodeId = nodeId;

    await Promise.all(
      editor.getNodes().map(node => area.update('node', node.id))
    );

    notifySelection();
  };

  const removeNode = async (nodeId, { allowStart = false } = {}) => {
    const node = getNodeById(editor, nodeId);

    if (!node || (!allowStart && node.data?.type === 'start')) return false;

    const connectionsToRemove = editor
      .getConnections()
      .filter(item => item.source === nodeId || item.target === nodeId);

    await Promise.all(
      connectionsToRemove.map(item => editor.removeConnection(item.id))
    );
    await editor.removeNode(nodeId);

    if (selectedNodeId === nodeId) {
      selectedNodeId = null;
      notifySelection();
    }

    return true;
  };

  const onKeyDown = async event => {
    if (event.key !== 'Delete' || !selectedNodeId) return;

    await removeNode(selectedNodeId);
  };

  area.addPipe(context => {
    if (context.type === 'nodepicked') {
      setSelectedNode(context.data.id).catch(() => {});
    }

    if (context.type === 'contextmenu') {
      const targetNode = context.data.context?.id
        ? getNodeById(editor, context.data.context.id)
        : null;

      context.data.event.preventDefault();

      if (targetNode) {
        setSelectedNode(targetNode.id).catch(() => {});
      }

      onContextMenu({
        x: context.data.event.clientX,
        y: context.data.event.clientY,
        node: targetNode ? serializeNode(area, targetNode) : null,
      });
    }

    return context;
  });

  window.addEventListener('keydown', onKeyDown);

  const addNode = async (type, position = { x: 160, y: 160 }) => {
    const node = createNodeByType(socket, type);

    await editor.addNode(node);
    await area.translate(node.id, position);
    await setSelectedNode(node.id);

    return node;
  };

  const getConnectionCount = ({ sourceNodeId, sourceOutput }) =>
    editor
      .getConnections()
      .filter(
        item =>
          item.source === sourceNodeId && item.sourceOutput === sourceOutput
      ).length;

  const getNodePosition = nodeId =>
    area.nodeViews.get(nodeId)?.position || { x: 160, y: 160 };

  const getSpawnPosition = ({
    sourceNodeId,
    sourceOutput,
    siblingIndex = 0,
  }) => {
    const sourcePosition = getNodePosition(sourceNodeId);
    const baseX = sourcePosition.x + HORIZONTAL_GAP;

    if (sourceOutput === 'buttons' || sourceOutput === 'rows') {
      return {
        x: baseX,
        y: sourcePosition.y + siblingIndex * SPAWN_VERTICAL_GAP,
      };
    }

    return {
      x: baseX,
      y: sourcePosition.y,
    };
  };

  const getAutoCreateType = ({ sourceNode, sourceOutput }) => {
    if (!sourceNode) return 'text';

    if (sourceNode.data?.type === 'start' && sourceOutput === 'compose') {
      return 'interactive';
    }

    if (sourceNode.data?.type === 'interactive') {
      if (sourceOutput === 'buttons') return 'button';
      if (sourceOutput === 'list') return 'list_message';
      if (sourceOutput === 'commerce') return 'commerce';
      return 'text';
    }

    if (
      sourceNode.data?.type === 'list_message' &&
      sourceOutput === 'section'
    ) {
      return 'section';
    }

    if (sourceNode.data?.type === 'section' && sourceOutput === 'rows') {
      return 'row';
    }

    return 'text';
  };

  const connectNodes = async ({
    sourceNodeId,
    sourceOutput = 'next',
    targetNodeId,
    targetInput,
  }) => {
    const sourceNode = getNodeById(editor, sourceNodeId);
    const targetNode = getNodeById(editor, targetNodeId);
    const finalTargetInput = targetInput || getFirstInputKey(targetNode);

    if (!sourceNode || !targetNode) return;
    if (
      !canConnectNodes({
        sourceNode,
        sourceOutput,
        targetNode,
        targetInput: finalTargetInput,
      })
    ) {
      return;
    }

    if (sourceNode.data?.type === 'section' && sourceOutput === 'rows') {
      const existingRowConnections = editor
        .getConnections()
        .filter(
          item =>
            item.source === sourceNode.id &&
            item.sourceOutput === 'rows' &&
            getNodeById(editor, item.target)?.data?.type === 'row'
        ).length;

      if (existingRowConnections >= 10) return;
    }

    if (sourceNode.data?.type === 'interactive') {
      if (sourceOutput === 'buttons') {
        const existingButtonConnections = editor
          .getConnections()
          .filter(
            item =>
              item.source === sourceNode.id &&
              item.sourceOutput === 'buttons' &&
              getNodeById(editor, item.target)?.data?.type === 'button'
          ).length;

        if (existingButtonConnections >= MAX_INTERACTIVE_BUTTONS) return;

        const listConnections = editor
          .getConnections()
          .filter(
            item =>
              item.source === sourceNode.id && item.sourceOutput === 'list'
          );

        await Promise.all(
          listConnections.map(item =>
            removeNode(item.target, { allowStart: true })
          )
        );
      }

      if (sourceOutput === 'list') {
        const existingListConnections = editor
          .getConnections()
          .filter(
            item =>
              item.source === sourceNode.id &&
              item.sourceOutput === 'list' &&
              getNodeById(editor, item.target)?.data?.type === 'list_message'
          ).length;

        if (existingListConnections >= 1) return;

        const buttonConnections = editor
          .getConnections()
          .filter(
            item =>
              item.source === sourceNode.id && item.sourceOutput === 'buttons'
          );

        await Promise.all(
          buttonConnections.map(item =>
            removeNode(item.target, { allowStart: true })
          )
        );
      }
    }

    const duplicate = editor
      .getConnections()
      .find(
        item =>
          item.source === sourceNodeId &&
          item.sourceOutput === sourceOutput &&
          item.target === targetNodeId &&
          item.targetInput === finalTargetInput
      );

    if (duplicate) return;

    await editor.addConnection(
      new ClassicPreset.Connection(
        sourceNode,
        sourceOutput,
        targetNode,
        finalTargetInput
      )
    );
  };

  const addNodeConnectedToSelection = async type => {
    const selectedNode = selectedNodeId
      ? getNodeById(editor, selectedNodeId)
      : null;

    const selectedView = selectedNodeId
      ? area.nodeViews.get(selectedNodeId)
      : null;
    const position = selectedView
      ? getSpawnPosition({
          sourceNodeId: selectedNode.id,
          sourceOutput:
            selectedNode.data?.type === 'start' ? 'compose' : 'next',
          siblingIndex: getConnectionCount({
            sourceNodeId: selectedNode.id,
            sourceOutput:
              selectedNode.data?.type === 'start' ? 'compose' : 'next',
          }),
        })
      : { x: 320, y: 220 };

    const node = await addNode(type, position);

    if (selectedNode) {
      let sourceOutput = 'next';

      if (selectedNode.data?.type === 'start') {
        sourceOutput = 'compose';
      } else if (selectedNode.data?.type === 'interactive') {
        sourceOutput = Object.keys(selectedNode.outputs || {})[0];
      }

      if (sourceOutput) {
        await connectNodes({
          sourceNodeId: selectedNode.id,
          sourceOutput,
          targetNodeId: node.id,
        });
      }
    }

    return node;
  };

  const addNodeFromOutput = async ({ sourceNodeId, sourceOutput }) => {
    const sourceNode = getNodeById(editor, sourceNodeId);

    if (!sourceNode || !sourceNode.outputs?.[sourceOutput]) return null;

    const nextType = getAutoCreateType({ sourceNode, sourceOutput });
    if (
      sourceNode.data?.type === 'interactive' &&
      sourceOutput === 'buttons' &&
      getConnectionCount({
        sourceNodeId,
        sourceOutput: 'buttons',
      }) >= MAX_INTERACTIVE_BUTTONS
    ) {
      return null;
    }

    if (
      sourceNode.data?.type === 'interactive' &&
      sourceOutput === 'list' &&
      getConnectionCount({
        sourceNodeId,
        sourceOutput: 'list',
      }) >= 1
    ) {
      return null;
    }

    const siblingIndex = editor
      .getConnections()
      .filter(
        item =>
          item.source === sourceNodeId && item.sourceOutput === sourceOutput
      ).length;
    const targetPosition = getSpawnPosition({
      sourceNodeId,
      sourceOutput,
      siblingIndex,
    });
    const node = await addNode(nextType, targetPosition);

    await connectNodes({
      sourceNodeId,
      sourceOutput,
      targetNodeId: node.id,
    });

    if (sourceNode.data?.type === 'interactive' && sourceOutput === 'list') {
      const sectionNode = await addNode('section', {
        x: targetPosition.x + HORIZONTAL_GAP,
        y: targetPosition.y,
      });

      await connectNodes({
        sourceNodeId: node.id,
        sourceOutput: 'section',
        targetNodeId: sectionNode.id,
      });

      const rowNode = await addNode('row', {
        x: targetPosition.x + HORIZONTAL_GAP * 2,
        y: targetPosition.y,
      });

      await connectNodes({
        sourceNodeId: sectionNode.id,
        sourceOutput: 'rows',
        targetNodeId: rowNode.id,
      });
    }

    return serializeNode(area, node);
  };

  const arrangeNodes = async () => {
    const nodes = editor.getNodes();
    if (!nodes.length) return;

    const connections = editor.getConnections();
    const childrenBySource = new Map();

    connections.forEach(connectionItem => {
      const items = childrenBySource.get(connectionItem.source) || [];
      items.push(connectionItem);
      childrenBySource.set(connectionItem.source, items);
    });

    const levels = new Map();
    const rows = new Map();
    const queue = [];
    const visited = new Set();

    const startNodes = getStartNodes(editor);
    const rootNodes = startNodes.length ? startNodes : [nodes[0]];

    rootNodes.forEach((node, index) => {
      levels.set(node.id, 0);
      rows.set(node.id, index);
      queue.push(node);
      visited.add(node.id);
    });

    while (queue.length) {
      const currentNode = queue.shift();
      const currentLevel = levels.get(currentNode.id) || 0;
      const currentRow = rows.get(currentNode.id) || 0;
      const outgoingConnections = childrenBySource.get(currentNode.id) || [];

      outgoingConnections.forEach((connectionItem, index) => {
        const targetNode = getNodeById(editor, connectionItem.target);
        if (!targetNode) return;

        const nextLevel = currentLevel + 1;
        const nextRow = currentRow + index;

        if (
          !levels.has(targetNode.id) ||
          nextLevel < (levels.get(targetNode.id) || Number.MAX_SAFE_INTEGER)
        ) {
          levels.set(targetNode.id, nextLevel);
          rows.set(targetNode.id, nextRow);
        }

        if (!visited.has(targetNode.id)) {
          queue.push(targetNode);
          visited.add(targetNode.id);
        }
      });
    }

    const unvisitedNodes = nodes.filter(node => !levels.has(node.id));
    const baseRow =
      Math.max(...Array.from(rows.values(), value => value), 0) + 1;

    unvisitedNodes.forEach((node, index) => {
      levels.set(node.id, 0);
      rows.set(node.id, baseRow + index);
    });

    const nodesByLevel = new Map();
    nodes.forEach(node => {
      const level = levels.get(node.id) || 0;
      const items = nodesByLevel.get(level) || [];
      items.push(node);
      nodesByLevel.set(level, items);
    });

    const sortedLevels = Array.from(nodesByLevel.keys()).sort((a, b) => a - b);

    await Promise.all(
      sortedLevels.flatMap(level => {
        const levelNodes = (nodesByLevel.get(level) || []).sort((a, b) => {
          return (rows.get(a.id) || 0) - (rows.get(b.id) || 0);
        });

        return levelNodes.map((node, index) =>
          area.translate(node.id, {
            x: 80 + level * HORIZONTAL_GAP,
            y: 140 + index * VERTICAL_GAP,
          })
        );
      })
    );

    AreaExtensions.zoomAt(area, editor.getNodes());
  };

  const updateNodeData = async (nodeId, patch) => {
    const node = getNodeById(editor, nodeId);

    if (!node) return;

    node.data = {
      ...node.data,
      ...patch,
    };

    if (patch.title && node.data?.type !== 'start') {
      node.label = patch.title;
    }

    if (
      node.data?.type === 'interactive' &&
      ['subtype', 'buttons', 'sections', 'listButtonLabel'].some(key =>
        Object.prototype.hasOwnProperty.call(patch, key)
      )
    ) {
      syncInteractiveOutputs(node, socket);

      const validOutputs = new Set(Object.keys(node.outputs || {}));
      const invalidConnections = editor
        .getConnections()
        .filter(
          item =>
            item.source === node.id && !validOutputs.has(item.sourceOutput)
        );

      await Promise.all(
        invalidConnections.map(item => editor.removeConnection(item.id))
      );
    }

    await area.update('node', node.id);
    notifySelection();
  };

  const load = async flow => {
    await editor.clear();

    selectedNodeId = null;
    onContextMenu(null);

    const nodesByOriginalId = new Map();
    const rawNodes = flow.nodes || [];
    const firstStartIndex = rawNodes.findIndex(item => item.type === 'start');
    const nodes = rawNodes.filter((item, index) => {
      if (item.type !== 'start') return true;

      return firstStartIndex === -1 ? false : index === firstStartIndex;
    });
    const connections = flow.connections || [];

    const createdNodes = await Promise.all(
      nodes.map(async item => {
        const node = createNodeByType(socket, item.type);

        node.label = item.label || node.label;
        node.data = {
          ...node.data,
          ...(item.data || {}),
        };

        if (node.data?.type === 'interactive') {
          syncInteractiveOutputs(node, socket);
        }

        await editor.addNode(node);
        await area.translate(node.id, item.position || { x: 0, y: 0 });

        return { originalId: item.id, nodeId: node.id };
      })
    );

    createdNodes.forEach(item => {
      nodesByOriginalId.set(item.originalId, item.nodeId);
    });

    await Promise.all(
      connections.map(item =>
        connectNodes({
          sourceNodeId: nodesByOriginalId.get(item.from),
          sourceOutput: item.fromOutput,
          targetNodeId: nodesByOriginalId.get(item.to),
          targetInput: item.toInput,
        })
      )
    );

    let [startNode] = getStartNodes(editor);

    if (!startNode) {
      startNode = await addNode('start', { x: 80, y: 140 });
    }

    const duplicateStartNodes = getStartNodes(editor).filter(
      node => node.id !== startNode.id
    );

    if (duplicateStartNodes.length) {
      await Promise.all(
        duplicateStartNodes.map(node =>
          removeNode(node.id, { allowStart: true })
        )
      );
    }

    if (editor.getNodes().length === 1) {
      const interactiveNode = await addNode('interactive', { x: 730, y: 145 });

      await connectNodes({
        sourceNodeId: startNode.id,
        sourceOutput: 'compose',
        targetNodeId: interactiveNode.id,
      });
    }

    AreaExtensions.zoomAt(area, editor.getNodes());
    await setSelectedNode(startNode?.id || editor.getNodes()[0]?.id || null);
  };

  const serialize = () => ({
    nodes: editor.getNodes().map(node => serializeNode(area, node)),
    connections: editor.getConnections().map(item => ({
      id: item.id,
      from: item.source,
      fromOutput: item.sourceOutput,
      to: item.target,
      toInput: item.targetInput,
    })),
  });

  await load({});

  return {
    addNodeConnectedToSelection,
    arrangeNodes,
    async connectSockets({
      sourceNodeId,
      sourceOutput,
      targetNodeId,
      targetInput,
    }) {
      await connectNodes({
        sourceNodeId,
        sourceOutput,
        targetNodeId,
        targetInput,
      });
    },
    destroy() {
      window.removeEventListener('keydown', onKeyDown);
      area.destroy();
    },
    getNode(nodeId) {
      const node = getNodeById(editor, nodeId);

      return node ? serializeNode(area, node) : null;
    },
    load,
    removeSelectedNode() {
      return selectedNodeId ? removeNode(selectedNodeId) : false;
    },
    async selectNode(nodeId) {
      await setSelectedNode(nodeId);
    },
    serialize,
    async spawnNodeFromOutput(payload) {
      return addNodeFromOutput(payload);
    },
    updateNodeData,
  };
};
